import React from 'react';
import { ChevronLeft, Loader2, Check, Bookmark } from 'lucide-react';
import type { ThemeConfig } from '../../types/forms';
import { getButtonStyles } from '../../lib/themeUtils';

interface TypeformNavigationProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirst: boolean;
  isLast: boolean;
  isSubmitting: boolean;
  submitText?: string;
  nextText?: string;
  themeConfig?: ThemeConfig;
  onSaveExit?: () => void;
  isSavingExit?: boolean;
  canSaveExit?: boolean;
  /**
   * (saas-1.24-hotfix3) Quando true, renderiza a dica de atalho abaixo dos botões.
   * Default false. Aplicar apenas em campos de texto (text/email/phone/url/number/textarea).
   */
  showEnterHint?: boolean;
  /**
   * Campo de texto longo (textarea): Enter quebra linha, então o avanço é por
   * Ctrl+Enter / ⌘+Enter e a dica muda junto (mandato user 2026-08-14).
   */
  isLongText?: boolean;
}

// ⌘ no macOS/iOS, Ctrl no resto
const isApplePlatform =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const MODIFIER_LABEL = isApplePlatform ? '⌘' : 'Ctrl';

export const TypeformNavigation: React.FC<TypeformNavigationProps> = ({
  onNext,
  onPrevious,
  isFirst,
  isLast,
  isSubmitting,
  submitText,
  nextText,
  themeConfig,
  onSaveExit,
  isSavingExit,
  canSaveExit,
  showEnterHint = false,
  isLongText = false,
}) => {
  return (
    <div className="flex flex-col items-center gap-3 w-full max-w-xl mx-auto">
      {/* mobile-ui.6.D D1: chrome compacto no short — OK encolhe pra conteúdo (flex-none) e o
          trio centraliza; alvos nunca <40px (D4) */}
      <div className="flex items-center gap-3 w-full short:justify-center">
        {!isFirst && (
          <button
            type="button"
            onClick={onPrevious}
            aria-label="Voltar"
            className="flex items-center justify-center w-12 h-12 short:w-10 short:h-10 border bg-white/4 hover:bg-white/8 hover:border-white/30 hover:text-white focus:outline-none focus:ring-2 transition-all duration-200 shrink-0"
            style={{
              borderRadius: `${themeConfig?.buttonBorderRadius ?? 9999}px`,
              borderColor: 'rgba(255,255,255,0.12)',
              color: themeConfig?.textColor || '#a3a3a3',
              ['--tw-ring-color' as string]: `${themeConfig?.buttonBgColor || '#3b82f6'}40`,
            }}
          >
            <ChevronLeft className="w-5 h-5 short:w-4 short:h-4" />
          </button>
        )}

        {/* D1: getButtonStyles injeta padding/fontSize/minHeight INLINE (tema) — o short usa
            utilities com `!` (important vence inline) pra encolher SÓ dimensões; cores/estilo
            do tema seguem valendo (AC4) */}
        <button
          type="button"
          onClick={onNext}
          disabled={isSubmitting}
          style={getButtonStyles(themeConfig)}
          className="flex-1 short:flex-none short:min-h-10! short:py-2! short:px-4! short:text-sm! flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-200"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando...
            </>
          ) : isLast ? (
            <>
              {submitText || 'Enviar'}
              <Check className="w-4 h-4" />
            </>
          ) : (
            <>
              {nextText || 'OK'}
              <Check className="w-4 h-4" />
            </>
          )}
        </button>

        {/* Save & Exit — discrete button, only when partial save is enabled (saas-1.24.A AC-A6: mesma skin secundária do voltar) */}
        {onSaveExit && (
          <button
            type="button"
            onClick={onSaveExit}
            disabled={!canSaveExit || isSavingExit}
            aria-label="Salvar e sair"
            className="flex items-center gap-1.5 px-3 py-2.5 border bg-white/4 hover:bg-white/8 hover:border-white/30 hover:text-white focus:outline-none focus:ring-2 transition-all duration-200 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              borderRadius: `${themeConfig?.buttonBorderRadius ?? 9999}px`,
              borderColor: 'rgba(255,255,255,0.12)',
              color: themeConfig?.textColor || '#a3a3a3',
              ['--tw-ring-color' as string]: `${themeConfig?.buttonBgColor || '#3b82f6'}40`,
            }}
          >
            {isSavingExit ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Bookmark className="w-4 h-4" />
            )}
            <span className="text-xs hidden sm:inline">Salvar e sair</span>
          </button>
        )}
      </div>

      {/* mobile-ui.6.D D3: hint redundante com o botão visível — some no short */}
      {showEnterHint && (
        <span className="text-xs text-neutral-500 short:hidden">
          Pressione{' '}
          <strong className="text-neutral-400">
            {isLongText ? `${MODIFIER_LABEL} + Enter` : 'Enter'} ↵
          </strong>
        </span>
      )}
    </div>
  );
};
